import React, { useEffect } from 'react';
import './Disclaimer.css';

const Disclaimer = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  return (
    <main className="disclaimer-page">
      <div className="container disclaimer-container">
        <header className="disclaimer-header">
          <h1>Disclaimer</h1>
          <p className="disclaimer-subtitle">
            Scan2Card &ndash; AI-Powered Lead Capture &amp; Business Card Scanner
          </p>
          <p className="disclaimer-meta">
            Scanbiz Technologies LLP | <a href="https://scan2card.com">scan2card.com</a>
          </p>
          <p className="disclaimer-meta">
            <strong>Last updated:</strong> 03/07/2026
          </p>
        </header>

        <section className="disclaimer-intro">
          <p>
            This Disclaimer governs your use of the Scan2Card website (scan2card.com),
            mobile application and services (the &ldquo;Service&rdquo;), provided by{' '}
            <strong>Scanbiz Technologies LLP</strong> (&ldquo;Scanbiz&rdquo;,
            &ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;). By using the Service,
            you acknowledge that you have read, understood and accepted this Disclaimer
            together with our <a href="/terms-conditions">Terms &amp; Conditions</a> and{' '}
            <a href="/privacy-policy">Privacy Policy</a>, all of which should be read
            together and form part of the contractual framework governing your use of the
            Service.
          </p>
        </section>

        <section id="section-1" className="disclaimer-section">
          <h2>1. General information</h2>
          <p>
            The information and features provided through the Service are for general
            business and lead-management purposes only. While we strive for accuracy and
            reliability, we make no representations or warranties of any kind, express or
            implied, about the completeness, accuracy, reliability, suitability or
            availability of the Service or its content.
          </p>
        </section>

        <section id="section-2" className="disclaimer-section">
          <h2>2. &ldquo;As is&rdquo; / no warranty</h2>
          <p>
            The Service is provided on an &ldquo;as is&rdquo;, &ldquo;as available&rdquo;
            and &ldquo;as provided&rdquo; basis to the fullest extent permitted under
            applicable law. Scanbiz expressly disclaims all warranties, representations
            and conditions of any kind, whether express, implied, statutory or otherwise,
            including any implied warranties of merchantability, fitness for a particular
            purpose, title, non-infringement, accuracy, reliability, availability or
            uninterrupted operation.
          </p>
          <p>
            Scanbiz does not warrant that the Service will be uninterrupted, error-free,
            secure, free from viruses or other harmful components, or that any identified
            defects will be corrected within a specified period.
          </p>
        </section>

        <section id="section-3" className="disclaimer-section">
          <h2>3. OCR and AI accuracy</h2>
          <p>
            Scan2Card uses Optical Character Recognition (&ldquo;OCR&rdquo;), Artificial
            Intelligence (&ldquo;AI&rdquo;) and other automated technologies to extract,
            organise, translate and classify information from business cards, QR codes
            and digital profiles.
          </p>
          <p>
            Such technologies generate outputs automatically and may produce incomplete,
            inaccurate, outdated, misleading or incorrectly translated information.
          </p>
          <p>
            Users are solely responsible for independently reviewing, verifying and
            validating all extracted or AI-generated information before relying upon it
            for any commercial, legal, regulatory or business purpose.
          </p>
          <p>
            Scanbiz does not guarantee the completeness, accuracy or suitability of any
            AI-generated output and shall not be responsible for any decisions, actions
            or omissions arising from reliance on such output.
          </p>
        </section>

        <section id="section-4" className="disclaimer-section">
          <h2>4. Your responsibility for lawful data capture</h2>
          <p>
            You are solely responsible for ensuring that you have a lawful basis and any
            necessary consent to scan business cards and to record conversations,
            photographs or selfies of individuals through the Service. Scanbiz processes
            such Lead Data solely for the purpose of providing and maintaining the
            Service in accordance with the User&rsquo;s instructions and applicable law.
            Users remain solely responsible for ensuring that the collection and use of
            such Lead Data complies with applicable legal requirements, including
            obtaining any necessary notices or consents.
          </p>
        </section>

        <section id="section-5" className="disclaimer-section">
          <h2>5. Third-party services and links</h2>
          <p>
            The Service relies on and may link to third-party services (including Amazon
            Web Services, Google Calendar/Meet, WhatsApp, OCR/AI and payment providers).
            We do not control and are not responsible for the content, policies,
            availability or practices of any third party. Their use is at your own risk
            and subject to their terms. Scanbiz shall not be liable for any interruption,
            modification, suspension or discontinuation of any third-party service or for
            any resulting impact on the availability or functionality of the Service.
          </p>
        </section>

        <section id="section-6" className="disclaimer-section">
          <h2>6. No professional advice</h2>
          <p>
            Nothing in the Service constitutes legal, financial, tax or other
            professional advice. You should obtain independent professional advice before
            making decisions based on data or analytics from the Service.
          </p>
        </section>

        <section id="section-7" className="disclaimer-section">
          <h2>7. Certifications</h2>
          <p>
            Our information-security and quality certifications (<strong>ISO/IEC
            27001:2022</strong>, Certificate No. SG/IAS/IS/202420, and{' '}
            <strong>ISO 9001:2015</strong>, Certificate No. SG/IAS/QM/202383, issued by
            Shamkris Global Inspection Services Pvt. Ltd.) reflect audited management
            systems but are not a guarantee against all risks. Certificates are valid
            only while the required annual surveillance audits are maintained.
          </p>
        </section>

        <section id="section-8" className="disclaimer-section">
          <h2>8. Limitation of liability</h2>
          <p>
            To the fullest extent permitted under applicable law, Scanbiz shall not be
            liable for any indirect, incidental, consequential, exemplary, special or
            punitive damages, or for any loss of profits, revenue, goodwill, business
            opportunity, anticipated savings or data arising out of or relating to the
            use of, inability to use or reliance upon the Service or its outputs.
          </p>
          <p>
            Nothing contained in this Disclaimer shall expand Scanbiz&rsquo;s liability
            beyond that expressly set out in the Terms &amp; Conditions. In the event of
            any inconsistency, the limitation of liability provisions contained in the
            Terms &amp; Conditions shall prevail.
          </p>
        </section>

        <section id="section-9" className="disclaimer-section">
          <h2>9. Contact</h2>
          <p>Questions about this Disclaimer:</p>
          <address>
            Scanbiz Technologies LLP,<br />
            Office No. 236, 2nd Floor, Adarsh Industrial Estate,<br />
            Sahar Airport Road, Chakala, Andheri (East),<br />
            Mumbai &ndash; 400099, Maharashtra, India.
          </address>
          <p>
            <strong>Tel.:</strong> +91 72638 89831 &nbsp;|&nbsp;{' '}
            <strong>E-mail:</strong>{' '}
            <a href="mailto:aryan@scan2card.com">aryan@scan2card.com</a>
          </p>
        </section>
      </div>
    </main>
  );
};

export default Disclaimer;
